// ============================================================
// FILE: Magnati.tsx
// ============================================================

import React, { useEffect, useState } from "react";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

import DialogManageUser from "@/app/models/dialogs/DialogMangeUser";
import UniInput from "@/app/components/common/actions/inputs-b/UniInput";
import { UniDropdown } from "@/app/components/common/actions/inputs-b/UniDropdown";
import { ICON_BASE_PATH } from "@/app/utils/config";

import "../ThirdParty.css";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import { RequestConfirmation } from "@/app/components/common/alert/RequestConfirmation";
import { useAdminContext } from "@/app/services/context/AdminContext";
import { useThirdPartySubFeatureUpdate } from "@/app/hooks/useAdminApiHooks";
import { FIELD_ICONS } from "../ThirdPartyFieldIcons";

// --------------------------------------------------------------
// "Help" content shown in the info popup.
// --------------------------------------------------------------
const MagnatiHelpContent = () => (
  <div className="gx-p14-m gx-help-content">
    <p>
      To connect your Magnati account, you'll need a few credentials from
      your Magnati Merchant Portal:
    </p>

    <ol className="gx-help-list">
      <li>Log in to your Magnati Merchant Portal.</li>

      <li>
        Go to <strong>Settings → API Credentials</strong>.
        <ol>
          <li>
            Copy your <strong>Merchant ID</strong>.
          </li>
          <li>
            Copy your <strong>Secret Key</strong>.
          </li>
        </ol>
      </li>

      <li>
        Next, go to <strong>Settings → Webhooks</strong>.
        <ol>
          <li>
            Add a new endpoint using the webhook URL provided in this platform.
          </li>
          <li>
            After creating it, open the webhook to reveal the{" "}
            <strong>Signing Secret</strong>.
          </li>
          <li>
            Copy the <strong>Webhook Secret</strong>.
          </li>
        </ol>
      </li>

      <li>
        Paste all the above values into the respective fields here and submit
        your request.
      </li>
    </ol>
  </div>
);

// --------------------------------------------------------------
// Static field configuration.
// These maps are keyed by the `fieldName` value coming back from
// `compConfigFeatureFeeList`, so the backend controls which fields
// render, while the UI controls how each field looks/behaves.
// FIELD_ICONS now lives in the shared ThirdPartyFieldIcons file so
// Zoho/Stripe/Magnati all read from one source of truth.
// --------------------------------------------------------------
const DROPDOWN_OPTIONS: Record<string, string[]> = {};
const FIELD_TOOLTIPS: Record<string, string> = {};

// Builds the per-field required-field message, e.g. "Please enter Merchant ID".
const getRequiredFieldMessage = (fieldName: string) =>
  `Please enter ${fieldName}`;

const Magnati = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);

  // Current values for every dynamic field, keyed by fieldName.
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  // Validation errors for empty/invalid fields, keyed by fieldName.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const { selectedThirdPartySubFeature } = useAdminContext();
  const { mutate: updateThirdPartySubFeature } =
    useThirdPartySubFeatureUpdate();

  // Already-connected (recordState === "A") sub-features are read-only
  // here - the user is just viewing what's connected, so fields get
  // locked and the footer (Submit) is hidden.
  const isConnected = selectedThirdPartySubFeature?.recordState === "A";

  // Seed field values from the backend-driven config list whenever the
  // selected sub-feature changes. Errors are cleared on every re-seed so
  // stale validation messages don't survive a feature switch.
  useEffect(() => {
    const list = selectedThirdPartySubFeature?.compConfigFeatureFeeList ?? [];
    const initial: Record<string, string> = {};
    list.forEach((field) => {
      initial[field.fieldName] = field.fieldValue ?? "";
    });
    setFieldValues(initial);
    setFieldErrors({});
  }, [selectedThirdPartySubFeature]);

  // Updates a single field's value and clears its error as soon as the
  // user starts fixing it (so the message doesn't linger after a fix).
  const handleFieldChange = (name: string, val: string) => {
    setFieldValues((prev) => ({ ...prev, [name]: val }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  // Checks every field currently rendered and returns a map of
  // fieldName -> error message for any field that's empty/whitespace-only.
  const validateRequiredFields = (): Record<string, string> => {
    const list = selectedThirdPartySubFeature?.compConfigFeatureFeeList ?? [];
    const errors: Record<string, string> = {};

    list.forEach((field) => {
      const value = fieldValues[field.fieldName] ?? "";
      if (!value.trim()) {
        errors[field.fieldName] = getRequiredFieldMessage(field.fieldName);
      }
    });

    return errors;
  };

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    // Already connected - view-only, nothing to submit.
    if (isConnected) return;

    if (!selectedThirdPartySubFeature) {
      console.error(
        "[Magnati] No selectedThirdPartySubFeature in context - cannot submit.",
      );
      return;
    }

    // Block submission and surface inline errors if any required field
    // is empty.
    const errors = validateRequiredFields();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    // Build the update payload, preserving backend identifiers and
    // only overriding fieldValue with what the user typed.
    const updatedCompConfigFeatureFeeList = (
      selectedThirdPartySubFeature.compConfigFeatureFeeList ?? []
    ).map((field) => ({
      smeConfFeatureFeeId: field.smeConfFeatureFeeId,
      smeFeatureFeeId: field.smeFeatureFeeId,
      groupSmeCompId: selectedThirdPartySubFeature.groupSmeCompId,
      subFeatureId: field.subFeatureId,
      subFeatureConfigId: field.subFeatureConfigId,
      cifId: selectedThirdPartySubFeature.cifId ?? null,
      fieldName: field.fieldName,
      fieldValue: fieldValues[field.fieldName] ?? field.fieldValue,
      redirectUrl: field.redirectUrl,
    }));

    const payload = {
      smeFeatureFeeId: selectedThirdPartySubFeature.smeFeatureFeeId,
      featureId: selectedThirdPartySubFeature.featureId,
      paramConfigFlag: selectedThirdPartySubFeature.paramConfigFlag,
      compConfigFeatureFeeList: updatedCompConfigFeatureFeeList,
    };

    try {
      setIsLoading(true);
      const responseData = await updateThirdPartySubFeature(payload);

      if (responseData?.data) {
        setShowSubmitted(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    navigate(-1);
  };

  const handleCloseSubmitted = () => {
    setShowSubmitted(false);
    navigate(-1);
  };

  return (
    <div className="gx-erp-container">
      {isLoading && <SubmitLoading />}

      {showSubmitted && (
        <RequestConfirmation
          actionType="submitted"
          onConfirm={() => handleCloseSubmitted()}
          onClose={handleCloseSubmitted}
          title="Your Magnati request has been submitted"
          message="Thank you for choosing GrowX. Our team will reach out to you at your registered contact number soon with further details regarding your request."
        />
      )}

      {showHelp && (
        <RequestConfirmation
          actionType="help"
          title="How to get this information"
          onConfirm={() => setShowHelp(false)}
          onClose={() => setShowHelp(false)}
          requiredContent={<MagnatiHelpContent />}
        />
      )}

      <DialogManageUser
        isOpen={isOpen}
        handleHeaderClose={handleCloseModal}
        handleFooterSubmit={handleSubmit}
        title={t("request-to-connect")}
        subtitle={t("magnati")}
        submit={t("submit_request")}
        cancel=""
        userRoll=""
        userStatus=""
        handleAcitiveUsers={() => console.log("Activate users")}
        hideFooter={isConnected}
      >
        <div>
          <div className="gx-connect-header">
            <div className="gx-connect-header-text">
              <h1 className="gx-p18-sb">{t("connect-magnati")}</h1>
              <p className="gx-p12-m">
                Securely link your Magnati account to start accepting
                payments, manage transactions, and streamline your billing
                operations within the platform.
              </p>
            </div>

            <div className="gx-how-to-get" onClick={() => setShowHelp(true)}>
              <img
                src={`${ICON_BASE_PATH}interrogation-mark.svg`}
                alt="Info Icon"
              />
              <p className="gx-p14-sb">Help</p>
            </div>
          </div>

          <form className="gx-third-party-form" onSubmit={handleSubmit}>
            <div className="gx-third-party-form-grid">
              {selectedThirdPartySubFeature?.compConfigFeatureFeeList?.map(
                (field) => {
                  const commonProps = {
                    key: field.subFeatureConfigId,
                    fieldName: field.fieldName,
                    label: field.fieldName,
                    value: fieldValues[field.fieldName] ?? "",
                    onValidate: (name: string, isValid: boolean) =>
                      console.log(`Validation for ${name}: ${isValid}`),
                    validationMessage: fieldErrors[field.fieldName] ?? "",
                    tooltip: FIELD_TOOLTIPS[field.fieldName],
                    tooltipFloat: "down" as const,
                  };

                  if (field.fieldType === "DROPDOWN") {
                    return (
                      <UniDropdown
                        {...commonProps}
                        onChange={(val) =>
                          handleFieldChange(field.fieldName, val)
                        }
                        data={DROPDOWN_OPTIONS[field.fieldName] ?? []}
                        showStarLabel
                        iconSrc={FIELD_ICONS[field.fieldName] ?? "state.svg"}
                        minLength={3}
                        max={field.fieldLength > 0 ? field.fieldLength : 50}
                        disable={true}
                      />
                    );
                  }

                  return (
                    <UniInput
                      {...commonProps}
                      onChange={(val) =>
                        handleFieldChange(field.fieldName, val)
                      }
                      iconFile={FIELD_ICONS[field.fieldName]}
                      minLength={3}
                      max={field.fieldLength > 0 ? field.fieldLength : 40}
                      type={
                        field.fieldType === "PASSWORD FIELD"
                          ? "password"
                          : "text"
                      }
                      disable={isConnected}
                    />
                  );
                },
              )}
            </div>

            {/* Hidden submit lets DialogManageUser's footer button (which
                calls handleFooterSubmit) trigger native form submission.
                No-op when connected since the footer itself is hidden. */}
            <button type="submit" className="gx-hidden-submit" />
          </form>
        </div>
      </DialogManageUser>
    </div>
  );
};

export default Magnati;